import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import HeadingDots from './HeadingDots';

const grades = [
  { label: 'Preschool', age: 'Age 3–5 years old', img: '/spiick_imgs/grades/preschool.png', bg: '#d23a36', dark: true },
  { label: 'Grade 1', age: 'Age 6–7 years old', img: '/spiick_imgs/grades/grade1.png', bg: '#eea742', dark: true },
  { label: 'Grade 2', age: 'Age 7–8 years old', img: '/spiick_imgs/grades/grade2.png', bg: '#e27470', dark: true },
  { label: 'Grade 3', age: 'Age 8–9 years old', img: '/spiick_imgs/grades/grade3.png', bg: '#89bf57', dark: true },
  { label: 'Grade 4', age: 'Age 9–10 years old', img: '/spiick_imgs/grades/grade4.png', bg: '#5ebbc3', dark: true },
  { label: 'Grade 5', age: 'Age 10–11 years old', img: '/spiick_imgs/grades/grade5.png', bg: '#2f3663', dark: true }
];

export default function AgeGrid() {
  return (
    <section className="relative w-full bg-white pt-10 lg:pt-14 pb-10 lg:pb-14">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="text-center">
          <h2
            className="text-[52px] sm:text-[60px] lg:text-[72px] leading-[1.02] text-[#0c0a1a] tracking-tight"
            style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            A language program<br />tailored to{' '}
            <span className="relative inline-block align-baseline whitespace-nowrap">
              <svg
                aria-hidden="true"
                viewBox="0 0 220 80"
                preserveAspectRatio="none"
                className="absolute -left-[4%] -right-[4%] top-[14%] -bottom-[10%] w-[108%] h-[96%] z-0 pointer-events-none"
              >
                <path
                  d="M9 32 C 30 22, 70 18, 112 16 C 150 14, 190 18, 213 26 C 218 38, 214 54, 208 64 C 175 72, 130 71, 92 70 C 60 70, 24 68, 7 60 C 2 50, 4 38, 9 32 Z"
                  fill="#abf1b2"
                  opacity="0.72"
                />
              </svg>
              <span className="relative z-10" style={{ color: '#004867' }}>every age</span>
            </span>
          </h2>
          <p
            className="mt-6 max-w-2xl mx-auto text-[16px] text-[#555266] leading-relaxed"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
          >
            Start early, grow bilingual. Spiick adapts to every age with content and speaking practice designed to help children acquire English naturally as they grow.
          </p>
        </div>

        <div className="mt-14 lg:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {grades.map((g) => {
            const isDark = !!g.dark;
            const tileStyle = g.bg ? { backgroundColor: g.bg, '--tile-bg': g.bg } : {};
            const textColor = isDark ? 'text-white' : 'text-[#0c0a1a]';
            const subColor = isDark ? 'text-white/85' : 'text-[#666374]';
            const arrowBase = isDark
              ? 'border-white/40 text-white group-hover:bg-white group-hover:border-white group-hover:[color:var(--tile-bg)]'
              : 'border-[#0c0a1a]/15 text-[#0c0a1a] group-hover:bg-[#0c0a1a] group-hover:text-white group-hover:border-[#0c0a1a]';
            const baseClass = isDark
              ? 'group relative flex items-stretch overflow-hidden rounded-[24px] transition-colors duration-300 h-[220px] lg:h-[240px] hover:brightness-95'
              : 'group relative flex items-stretch overflow-hidden rounded-[24px] bg-[#F4F2F8] hover:bg-[#EAE4F7] transition-colors duration-300 h-[220px] lg:h-[240px]';
            return (
              <a
                key={g.label}
                href="#"
                style={tileStyle}
                className={baseClass}
              >
                {/* LEFT — text + arrow */}
                <div className="relative z-10 flex flex-col justify-between p-6 lg:p-7 w-1/2">
                  <div>
                    <h3
                      className={`text-[30px] lg:text-[30px] tracking-tight leading-tight ${textColor}`}
                      style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, letterSpacing: '-0.02em' }}
                    >
                      {g.label}
                    </h3>
                    <p
                      className={`mt-1 text-[14px] ${subColor}`}
                      style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                    >
                      {g.age}
                    </p>
                  </div>

                  <span
                    aria-hidden="true"
                    className={`w-11 h-11 rounded-full border grid place-items-center transition-colors duration-300 ${arrowBase}`}
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </span>
                </div>

                {/* RIGHT — character image, overflowing */}
                <div className="relative w-1/2 overflow-visible">
                  <img
                    src={g.img}
                    alt={g.label}
                    className="absolute -bottom-2 -right-3 lg:-right-4 h-[112%] w-auto max-w-none object-contain pointer-events-none select-none transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
